import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  FiHome, FiSearch, FiRadio, FiBriefcase, FiArchive, FiCalendar,
  FiFileText, FiUsers, FiZap, FiMessageSquare, FiEye, FiTarget,
  FiClipboard, FiRepeat, FiCpu, FiDollarSign, FiFolder, FiGitBranch,
  FiLogOut, FiMenu, FiX, FiBell, FiUser, FiSettings, FiGrid,
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { abrirVnc } from '../utils/vnc';
import './Layout.css';

async function abrirCaptchaNoVnc(event) {
  event?.stopPropagation();
  // [FASE VNC-SSO/E15] SSO + URL canonica: pede o cookie ao backend e abre
  // /vnc/primary/. Sem popup Basic e sem rota legada /vnc/vnc.html.
  await abrirVnc('primary');
}

/* ---------- Menu definition ---------- */
const menuGroups = [
  {
    title: 'Principal',
    items: [
      { to: '/visao-geral', icon: <FiGrid />,        label: 'Visão Geral' },
      { to: '/',            icon: <FiHome />,        label: 'Dashboard' },
      { to: '/pesquisar',   icon: <FiSearch />,      label: 'Pesquisar' },
      { to: '/radar',       icon: <FiRadio />,       label: 'Radar' },
    ],
  },
  {
    title: 'Licitações',
    items: [
      { to: '/minhas',      icon: <FiBriefcase />,   label: 'Minhas Licitações' },
      { to: '/arquivo',     icon: <FiArchive />,     label: 'Arquivo' },
      { to: '/agenda',      icon: <FiCalendar />,    label: 'Agenda' },
    ],
  },
  {
    title: 'Gestão',
    items: [
      { to: '/empenhos',       icon: <FiFileText />,      label: 'Empenhos e Atas' },
      { to: '/participacoes',  icon: <FiUsers />,         label: 'Participações' },
      { to: '/disputa',        icon: <FiZap />,           label: 'Disputa de Lances' },
      { to: '/mensagens',      icon: <FiMessageSquare />, label: 'Mensagens' },
    ],
  },
  {
    title: 'Análise',
    items: [
      { to: '/monitoramento', icon: <FiEye />,       label: 'Monitoramento' },
      { to: '/concorrente',   icon: <FiTarget />,    label: 'Concorrente' },
      { to: '/pca',           icon: <FiClipboard />, label: 'PCA' },
      { to: '/pesquisa-precos', icon: <FiDollarSign />, label: 'Pesquisa de Preços' },
      { to: '/terceirizados', icon: <FiUsers />, label: 'Terceirizados' },
    ],
  },
  {
    title: 'Automação',
    items: [
      { to: '/recurso-automatico', icon: <FiRepeat />, label: 'Recurso Automático' },
      { to: '/ia-studio',          icon: <FiCpu />,    label: 'IA Studio' },
    ],
  },
  {
    title: 'Administrativo',
    items: [
      { to: '/financeiro',  icon: <FiDollarSign />, label: 'Financeiro' },
      { to: '/documentos',  icon: <FiFolder />,     label: 'Documentos' },
      { to: '/workflow',        icon: <FiGitBranch />,  label: 'Workflow' },
      { to: '/configuracoes',   icon: <FiSettings />,   label: 'Configuracoes' },
    ],
  },
];

/* ---------- Page title map ---------- */
const pageTitles = {
  '/':                  'Dashboard',
  '/visao-geral':       'Visão Geral',
  '/pesquisar':         'Pesquisar Licitações',
  '/radar':             'Radar de Oportunidades',
  '/minhas':            'Minhas Licitações',
  '/arquivo':           'Arquivo',
  '/agenda':            'Agenda',
  '/empenhos':          'Empenhos e Atas',
  '/participacoes':     'Participações',
  '/disputa':           'Disputa de Lances',
  '/mensagens':         'Mensagens',
  '/monitoramento':     'Monitoramento',
  '/concorrente':       'Concorrente',
  '/pca':               'PCA',
  '/pesquisa-precos':   'Pesquisa de Preços',
  '/terceirizados':     'Terceirizados',
  '/recurso-automatico':'Recurso Automático',
  '/ia-studio':         'IA Studio',
  '/financeiro':        'Financeiro',
  '/documentos':        'Documentos',
  '/workflow':          'Workflow',
  '/configuracoes':     'Configuracoes',
  '/perfil':            'Meu Perfil',
};

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notificacoes, setNotificacoes] = useState([]);
  const [naoLidas, setNaoLidas] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const notifRef = useRef(null);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchNotificacoes = async () => {
      try {
        const { data } = await api.get('/notificacoes');
        setNotificacoes(Array.isArray(data.data) ? data.data.slice(0, 10) : []);
        setNaoLidas(data.naoLidas || 0);
      } catch (err) {
        console.error('Erro ao buscar notificacoes:', err);
      }
    };
    fetchNotificacoes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotif(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const marcarTodasLidas = async () => {
    try {
      await api.put('/notificacoes/ler-todas');
      setNaoLidas(0);
      setNotificacoes(prev => prev.map(n => ({ ...n, lida: true })));
    } catch (err) {
      console.error('Erro ao marcar notificacoes como lidas:', err);
    }
  };

  const formatNotifDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const handleSearch = () => {
    const q = searchTerm.trim();
    if (q) {
      navigate(`/pesquisar?q=${encodeURIComponent(q)}`);
      setSearchTerm('');
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const pageTitle = pageTitles[location.pathname] || 'LicitaPRO';
  const initials = user?.nome
    ? user.nome.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
    : 'U';

  return (
    <>
      {/* ---- Mobile overlay ---- */}
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* ---- Sidebar ---- */}
      <aside className={`layout-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-logo">
          <button className="toggle-btn" onClick={() => setCollapsed(c => !c)}>
            {collapsed ? <FiMenu /> : <FiX />}
          </button>
          {!collapsed && <span>LicitaPRO</span>}
        </div>

        <nav className="sidebar-nav">
          {menuGroups.map(group => (
            <div key={group.title}>
              <div className="sidebar-group-title">{group.title}</div>
              {group.items.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? 'active' : ''}`
                  }
                >
                  {item.icon}
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-logout">
          <button onClick={logout}>
            <FiLogOut />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      {/* ---- Header ---- */}
      <header className={`layout-header ${collapsed ? 'shifted' : ''}`}>
        <button className="mobile-menu-btn" onClick={() => setMobileOpen(true)}>
          <FiMenu />
        </button>
        <div className="header-title">{pageTitle}</div>
        <div className="header-search">
          <FiSearch className="header-search-icon" onClick={handleSearch} />
          <input
            type="text"
            className="header-search-input"
            placeholder="Buscar licitações..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
        </div>
        <div className="header-actions">
          <div className="notif-wrapper" ref={notifRef}>
            <button className="header-bell" title="Notificações" onClick={() => setShowNotif(v => !v)}>
              <FiBell />
              {naoLidas > 0 && <span className="notif-badge">{naoLidas > 99 ? '99+' : naoLidas}</span>}
            </button>
            {showNotif && (
              <div className="notif-dropdown">
                <div className="notif-dropdown-header">
                  <strong>Notificacoes</strong>
                  {naoLidas > 0 && (
                    <button className="notif-mark-all" onClick={marcarTodasLidas}>Marcar todas como lidas</button>
                  )}
                </div>
                {notificacoes.length === 0 ? (
                  <div className="notif-empty">Nenhuma notificacao.</div>
                ) : (
                  <ul className="notif-list">
                    {notificacoes.map(n => (
                      <li key={n.id} className={`notif-item ${!n.lida ? 'unread' : ''} ${n.tipo === 'alerta' ? 'notif-alerta' : 'notif-info'}`}>
                        <div className="notif-titulo">{n.titulo}</div>
                        <div className="notif-msg">{(n.mensagem || '').length > 80 ? n.mensagem.slice(0, 80) + '...' : n.mensagem}</div>
                        {n.canal === 'captcha:pendente' && (
                          <button type="button" className="notif-mark-all" onClick={abrirCaptchaNoVnc}>Abrir noVNC</button>
                        )}
                        <div className="notif-date">{formatNotifDate(n.createdAt)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <div className="header-user header-user-clickable" onClick={() => navigate('/perfil')} title="Meu Perfil">
            <div className="header-avatar">{initials}</div>
            <span>{user?.nome || 'Usuário'}</span>
          </div>
        </div>
      </header>

      {/* ---- Content ---- */}
      <main className={`layout-content ${collapsed ? 'shifted' : ''}`}>
        <Outlet />
      </main>
    </>
  );
}
